import { Sparkles } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"

/**
 * OKS-26 — Collab history block (UX Spec V2 §Collab history block).
 *
 * The EMPTY state of the member's collab list (OKS-1038): rendered by
 * `CrewMemberCollabsSection` when the member has no collab on this brand.
 * Loading is the section's skeleton, never this card.
 *
 * BE supplies `collab_history.empty_copy_key` so the i18n key namespace
 * stays under BE control. FE renders the localized string for that key.
 *
 * Locked testids:
 *   - `creator-detail-collab-history` — Card root
 *   - `creator-detail-collab-empty` — empty body
 */
interface CollabHistoryEmptyProps {
  /**
   * BE-supplied i18n key — V1 ships `"crew.detail.collab.empty_body"`
   * (FE i18n bundle defines this key). The indirection lets BE swap the
   * copy key (e.g. when missions partially populate) without an FE
   * release.
   */
  emptyCopyKey: string
}

/**
 * Hard-coded subset of legal `empty_copy_key` values. BE V1 only
 * surfaces `crew.detail.collab.empty_body`; future copy keys must be
 * added here AND to the i18n bundles. The literal `t()` calls keep
 * the inertia_i18n health scanner happy.
 */
function resolveEmptyCopy(
  t: ReturnType<typeof useTranslation>["t"],
  key: string,
): string {
  switch (key) {
    case "crew.detail.collab.empty_body":
      return t("crew.detail.collab.empty_body")
    default:
      // Defensive fallback: BE-supplied key resolved through `t()`
      // directly. Returning `t(key)` here means a typo'd or
      // unrecognized key surfaces as the literal key string in the UI
      // — preferable to throwing, which would crash the page on a
      // BE-side rename.
      return t(key)
  }
}

export function CollabHistoryEmpty({ emptyCopyKey }: CollabHistoryEmptyProps) {
  const { t } = useTranslation()
  return (
    <Card data-testid="creator-detail-collab-history">
      <CardHeader>
        <CardTitle>{t("crew.detail.collab.section_title")}</CardTitle>
      </CardHeader>
      <CardContent>
        <div
          data-testid="creator-detail-collab-empty"
          className="bg-muted/30 flex flex-col items-center gap-2 rounded-md border border-dashed p-6 text-center"
        >
          <Sparkles
            className="text-muted-foreground size-6"
            aria-hidden="true"
          />
          <p className="font-medium">{t("crew.detail.collab.empty_title")}</p>
          <p className="text-muted-foreground text-sm">
            {resolveEmptyCopy(t, emptyCopyKey)}
          </p>
        </div>
      </CardContent>
    </Card>
  )
}
